/** Contenido en español. Los nombres de modo siguen el catálogo `es.json`. */
import type { LanguageContent } from '../registry.js';

export const ES: LanguageContent = {
  home: {
    intro: [
      'Retusch es un juego de dibujo en línea para 2 a 10 jugadores, jugable en el navegador, sin instalar nada y sin cuenta. Cada ronda te entrega un cuadro al que se le ha borrado una casilla: repíntala para que desaparezca dentro de la obra. Después los papeles se invierten, y toca descubrir los retoques de los demás.',
      'La mayoría de los juegos de dibujo premian a quien se reconoce antes. Retusch pide lo contrario: ganar es no ser visto.',
    ],
    steps: [
      {
        title: 'Un cuadro con un hueco',
        text: 'Recibes una ilustración a la que le han borrado una casilla.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Tú la repintas',
        text: 'Vuelves a dibujar la casilla para que se funda con la obra.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'La caza',
        text: 'El cuadro regresa con los retoques de todos: encuéntralos antes que los demás.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'La revelación',
        text: 'Cada escondite se descubre, y caen los puntos.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Caza común',
        text: 'Todo el mundo pinta sobre el mismo cuadro, cada uno en su casilla. Después la caza se abre para todos a la vez: un retoque solo se encuentra una vez, por quien lo detecta primero. Es el modo por defecto, aquel en el que siempre está pasando algo.',
      },
      {
        name: 'Stream',
        text: 'El anfitrión no dibuja: busca, solo, mientras los demás se esconden en su cuadro. Pensado para el directo, donde la caza se convierte en el espectáculo. Gratis para siempre.',
      },
      {
        name: 'Un cuadro por jugador',
        text: 'Una obra por persona. Solo buscas el retoque de alguien, en un único clic. La regla más sencilla del juego, y la más tensa.',
      },
      {
        name: 'Solo uno dibuja',
        text: 'Una ronda, un dibujante, todos los demás persiguiéndolo. El papel rota, y el dibujante ve caer o resistir su propio escondite.',
      },
      {
        name: 'Battle royale',
        text: 'La misma caza, pero el primer retoque descubierto elimina a su autor. Se juega hasta el último en pie, sin contar un solo punto.',
      },
      {
        name: 'Concurso',
        text: 'Nadie busca. Todo el mundo repinta la misma casilla del mismo cuadro, y luego cada cual vota el retoque que más le gusta. Un voto recibido vale un punto.',
      },
    ],
    faq: [
      {
        question: '¿Cuántos jugadores hacen falta?',
        answer:
          'De 2 a 10. Si estás solo, un rival automático ocupa el asiento libre para que la partida empiece enseguida. A partir de cuatro, la caza común despliega todo su interés.',
      },
      {
        question: '¿Es gratis?',
        answer:
          'Sí. La partida rápida, la caza común y el modo stream son gratuitos, sin límite de tiempo ni de número de partidas. Los demás modos también lo son, por ahora.',
      },
      {
        question: '¿Hace falta crear una cuenta?',
        answer:
          'No. Escribes un apodo, haces clic y juegas. La cuenta solo sirve para conservar tu apodo y tu avatar de una partida a otra: es opcional.',
      },
      {
        question: '¿Funciona en el móvil?',
        answer:
          'Sí, la interfaz se adapta al táctil: barra de herramientas plegada, lupa bajo el dedo, doble toque para ampliar. Dicho esto, se dibuja con más precisión con ratón y una pantalla grande.',
      },
      {
        question: '¿Cómo invito a mis amigos?',
        answer:
          'Crea una sala, copia el enlace de invitación y pégalo donde quieras. Tus amigos llegan directamente a la mesa, sin cuenta y sin instalar nada.',
      },
      {
        question: '¿Qué es un «retoque»?',
        answer:
          'Es lo que pintas dentro de la casilla borrada. Bien hecho, pasa por un trozo del cuadro original y nadie lo encuentra. Mal hecho, salta a la vista.',
      },
    ],
    headings: {
      intro: 'Un juego de dibujo en el que hay que desaparecer',
      steps: 'Cómo se juega',
      modes: 'Seis formas de jugar',
      faq: 'Preguntas frecuentes',
      more: 'Para saber más',
    },
    links: {
      rules: 'Leer las reglas completas y la puntuación',
      modes: 'Comparar los modos en detalle',
    },
  },

  howToPlay: {
    title: 'Cómo jugar a Retusch',
    summary:
      'Las reglas completas de Retusch: el desarrollo de una ronda, la puntuación de cada lado, las herramientas de dibujo y los consejos para esconderse y para buscar.',
    sections: [
      {
        heading: 'La idea en tres frases',
        blocks: [
          'Te dan un cuadro al que le han borrado una casilla, y la repintas para que pase desapercibida.',
          'Después todo el mundo mira los cuadros retocados y busca las casillas repintadas.',
          'Puntúas encontrando rápido, y puntúas también mientras tu propio escondite aguante.',
        ],
      },
      {
        heading: 'Cómo se desarrolla una ronda',
        blocks: [
          'Una partida encadena rondas idénticas, cinco por defecto. Cada una pasa por tres momentos.',
          {
            list: [
              'El dibujo. Recibes tu cuadro y tu casilla, y tienes 2 minutos por defecto para repintarla. El botón Terminado equivale a entregar: en cuanto todos han entregado, la ronda avanza sin esperar al reloj.',
              'La caza. El cuadro reaparece con los retoques, y tienes 30 segundos por defecto para detectarlos. Un clic sobre una casilla repintada la revela a toda la mesa.',
              'La revelación. Todos los escondites quedan enmarcados y con nombre, incluidos los que nadie vio. Un cursor permite comparar el cuadro retocado con la obra original.',
            ],
          },
        ],
      },
      {
        heading: 'La puntuación',
        blocks: [
          'Dos contadores corren a la vez, y se oponen: quien busca quiere ir rápido, quien ha dibujado quiere que su casilla aguante.',
          {
            list: [
              'Como buscador: 1000 puntos si lo encuentras en los 3 primeros segundos, y después un descenso constante hasta 100 puntos al final de la fase. Fallar o no encontrar nada no da nada.',
              'Como dibujante: solo 100 puntos si tu casilla cae de inmediato, y después una subida constante hasta 800 puntos si aguanta hasta el final. Un retoque que nadie encuentra vale el máximo, 800 puntos.',
            ],
          },
          'Al dibujante se le paga una sola vez, en la revelación, y según el buscador más rápido. Da igual, por tanto, que te busquen dos personas u ocho: solo cuenta quien te encuentra primero.',
          'La puntuación es la misma sea cual sea la duración que fijes para la fase. Alargar la búsqueda no mueve los dos extremos, solo la pendiente entre ellos.',
          'Un solo error por ronda. El primer clic que no toca ningún retoque pone fin a tu búsqueda: sin esa regla, se rastrearía la imagen al azar hasta dar con algo. Dos gestos no cuestan nada: hacer clic en tu propia casilla, y hacer clic en un retoque ya encontrado.',
        ],
      },
      {
        heading: 'Las herramientas de dibujo',
        blocks: [
          {
            list: [
              'El pincel (B): tres tamaños rápidos, más un deslizador para el tamaño exacto. Los atajos 1, 2 y 3 eligen los tres tamaños.',
              'El bote (P): rellena de golpe una superficie lisa, sin salirse nunca de tu casilla.',
              'El cuentagotas (I): toma un color de cualquier punto del cuadro. Mantener Alt cambia a él durante un clic y devuelve luego la herramienta anterior.',
              'La mano (H): desplaza la vista. Fuera de tu casilla, el pincel y el bote hacen lo mismo, así que casi nunca necesitas cambiar de herramienta.',
              'Deshacer (Ctrl+Z o Cmd+Z): retrocede trazo a trazo, hasta treinta veces. No hay rehacer.',
              'El color de fondo, al final de la paleta: es el color inicial de tu casilla. Pintar con él equivale a borrar, salvo que es un trazo como cualquier otro, que se deshace y se cubre. Por eso no hay goma.',
            ],
          },
        ],
      },
      {
        heading: 'Cinco consejos para esconderse',
        blocks: [
          {
            list: [
              'Toma los colores, no los adivines. El cuentagotas da el color exacto del entorno: un plano apenas desviado se detecta de lejos, mientras que uno exacto se vuelve invisible.',
              'Prolonga lo que atraviesa tu casilla. Un muro, un horizonte, una rama que entra por un lado tiene que salir por el otro en el sitio correcto. Una línea interrumpida es la primera pista que busca un jugador con experiencia.',
              'Pinta primero las esquinas. Es el error más común: el centro está cuidado, las cuatro esquinas siguen con el fondo original, y el cuadrado se ve a simple vista sin ni siquiera mirar el dibujo.',
              'Ve de lo general al detalle. Coloca primero las grandes masas de color con un pincel grueso, y después afina. Al revés te comes todo el tiempo en una esquina mientras el resto queda vacío.',
              'Copia el nivel de detalle de alrededor. Una casilla demasiado limpia en medio de una zona cargada se ve tanto como una descuidada en medio de un plano liso.',
            ],
          },
        ],
      },
      {
        heading: 'Cuatro consejos para buscar',
        blocks: [
          {
            list: [
              'Busca las líneas que se cortan. Un borde, un cable, el canto de una mesa que se desplaza un pelo: casi siempre está ahí.',
              'Barre primero, amplía después. A vista completa, una casilla mal fundida aparece como una mancha. El zoom sirve para confirmar, no para buscar.',
              'Desconfía de lo demasiado liso. Una superficie perfectamente uniforme en medio de una textura es tan sospechosa como un borrón.',
              'No hagas clic al azar. Solo tienes derecho a un error: mejor perder tres segundos comprobando que perder la ronda entera.',
            ],
          },
        ],
      },
      {
        heading: '¿Y según el modo?',
        blocks: [
          'Todo lo anterior describe la caza común, el modo por defecto. Los demás modos cambian quién dibuja, quién busca y cómo se gana: el detalle de cada uno está en la página de modos de juego.',
        ],
      },
    ],
  },

  modes: {
    title: 'Los modos de juego de Retusch',
    summary:
      'Las seis formas de jugar a Retusch: caza común, stream, un cuadro por jugador, solo uno dibuja, battle royale y concurso. Desarrollo, número de jugadores recomendado y duración de cada una.',
    sections: [
      {
        heading: 'Caza común',
        blocks: [
          'El modo por defecto, y por el que conviene empezar.',
          'Todo el mundo dibuja sobre el mismo cuadro, cada uno en su propia casilla. Cuando los retoques están entregados, la obra vuelve entera y la caza se abre para todos a la vez. Un retoque solo se encuentra una vez, por quien lo detecta primero: entonces aparece para toda la mesa y sale del juego. Nunca encuentras el tuyo, y un escondite que nadie ve da el máximo a su autor.',
          'Ideal para un grupo grande: no hay tiempos muertos, todos buscan a la vez y cada hallazgo se ve en la pantalla de todos.',
          'Recomendado: de 3 a 10 jugadores.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'La imagen invertida del modo «solo uno dibuja», pensada para el directo.',
          'Todos los jugadores dibujan sobre el mismo cuadro, salvo el anfitrión. Después el anfitrión es el único que busca, mientras su público lo mira esforzarse. No suma puntos ni aparece en la clasificación: la partida se juega entre quienes se esconden, él dirige la caza. Puede equivocarse tantas veces como quiera, la regla del error único no se le aplica.',
          'Ideal para una velada en directo, y por una razón concreta: como el anfitrión no dibuja, los espectadores nunca ven construirse su escondite. En los demás modos lo ven pintar su casilla, y la caza deja de tener secretos para ellos.',
          'Recomendado: de 4 a 10 jugadores. Gratis, y seguirá siéndolo.',
        ],
      },
      {
        heading: 'Un cuadro por jugador',
        blocks: [
          'Cada jugador recibe su propia obra y esconde en ella su retoque. En la búsqueda te entregan el cuadro de otra persona, y tienes un único clic para señalar la casilla repintada. Sin carrera ni competencia: solo tú, un cuadro y una decisión.',
          'Ideal en pareja, o cuando se busca una partida tranquila en la que cada cual se tome su tiempo.',
          'Recomendado: de 2 a 10 jugadores.',
        ],
      },
      {
        heading: 'Solo uno dibuja',
        blocks: [
          'Una ronda, un dibujante, todos los demás persiguiéndolo.',
          'En cada ronda, una sola persona recibe un cuadro. Los demás esperan y luego buscan su retoque, cada uno puntuado según su propio tiempo. Al dibujante se le puntúa según el buscador más rápido, y ve su escondite congelado mientras lo rastrean. El papel rota: aquí el ajuste de rondas cuenta vueltas completas, porque una partida solo es justa si cada cual ha dibujado el mismo número de veces.',
          'Ideal para un grupo pequeño que quiera juzgarse en igualdad de condiciones, un dibujo tras otro.',
          'Recomendado: de 3 a 6 jugadores, más allá la espera se hace larga.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'La caza común, con una eliminación por ronda.',
          'Mismo cuadro compartido, misma búsqueda simultánea. Pero aquí no se cuenta ningún punto: la única pregunta es quién se queda. El jugador cuyo retoque se descubre primero abandona la partida. Los demás siguen buscando hasta el final de la ronda, lo que ya no elimina a nadie pero da a cada uno su puesto, mostrado sobre su casilla. Si no se encuentra a nadie, nadie sale.',
          'Ideal para cerrar una velada: la tensión sube a medida que la mesa se vacía, y la última ronda se juega entre dos.',
          'Recomendado: de 4 a 10 jugadores.',
        ],
      },
      {
        heading: 'Concurso',
        blocks: [
          'El único modo en el que nadie busca.',
          'Todo el mundo repinta la misma casilla del mismo cuadro. Después cada cual recorre los retoques de los demás, a su ritmo, y vota el que le parece mejor. Un voto recibido vale un punto a su autor. Durante la votación los autores son anónimos, y no se puede votar por uno mismo: los nombres y los votos solo salen en la revelación.',
          'Ideal cuando el grupo es variado, o cuando algunos prefieren dibujar sin la presión del reloj de la búsqueda.',
          'Recomendado: de 3 a 10 jugadores.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Caza común', players: '3 a 10', duration: '15 a 20 min' },
    { name: 'Stream', players: '4 a 10', duration: '15 a 20 min' },
    { name: 'Un cuadro por jugador', players: '2 a 10', duration: '15 a 20 min' },
    { name: 'Solo uno dibuja', players: '3 a 6', duration: '20 a 35 min' },
    { name: 'Battle royale', players: '4 a 10', duration: '20 a 30 min' },
    { name: 'Concurso', players: '3 a 10', duration: '10 a 15 min' },
  ],
  modeTableHeadings: ['Modo', 'Jugadores', 'Duración'],
  premium: {
    heading: 'Modos gratuitos y modos de pago',
    blocks: [
      'La caza común y el modo stream son gratuitos, de forma definitiva.',
      'Los otros cuatro pasarán a ser de pago al CREAR una partida, y solo al crearla. Unirse seguirá siendo siempre gratis: basta con que una persona de la mesa haya desbloqueado el juego para que todos puedan jugar a todos los modos, sin cuenta y sin pagar.',
      'El desbloqueo será una compra única, sin suscripción, y además quitará la publicidad para la cuenta que lo haya hecho. Mientras tanto, todos los modos están abiertos para todo el mundo.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: '¿Qué es Retusch?',
      date: '2026-08-21',
      summary:
        'Un juego de dibujo en línea para varios, cercano a Gartic Phone por la forma, pero cuya mecánica se parece a la de Meccha Chameleon: en vez de hacerse reconocer, se busca desaparecer.',
      sections: [
        {
          heading: 'Un juego de dibujo en el que ser reconocido es perder',
          blocks: [
            'Retusch es un juego de dibujo en línea para 2 a 10 jugadores. Cada ronda te entrega una ilustración a la que le han borrado una casilla, y tienes que repintarla para que se funda con la obra. Después todo el mundo mira los cuadros retocados y busca las casillas repintadas.',
            'Lo que lo distingue cabe en una frase: ganar es no ser visto.',
          ],
        },
        {
          heading: 'Lo que comparte con Gartic Phone',
          blocks: [
            'La forma es la misma: abres un enlace, escribes un apodo y juegas. Sin cuenta, sin instalación, sin cliente que descargar. El juego corre en el navegador y una partida arranca en segundos. Se invita a los amigos pegando un enlace en una conversación, y la mesa se llena.',
            'El ritmo también resulta familiar: rondas cortas, un cronómetro, una fase de dibujo y luego una fase en la que se descubre lo que han hecho los demás. Y sobre todo, esa revelación colectiva al final de la ronda en la que todos comentan lo que ven. Es el mismo placer de grupo.',
            'Como en Gartic Phone, no hace falta saber dibujar. Una casilla de cielo bien puesta gana a una obra maestra que se desborda.',
          ],
        },
        {
          heading: 'Lo que toma de Meccha Chameleon',
          blocks: [
            'En el fondo, sin embargo, la mecánica está en otra parte. En Meccha Chameleon, juego en tres dimensiones de Steam, pintas tu propio personaje y luego lo colocas en un decorado esperando que nadie lo distinga de lo que le rodea. La victoria viene del parecido y del buen emplazamiento, no de la acción.',
            'Retusch retoma esa idea y la devuelve a la imagen. No eliges dónde esconderte, es el juego quien te da la casilla; en cambio fabricas por completo lo que va dentro. El escondite funciona si la mirada resbala sobre él sin detenerse.',
            'Y como en todo juego de camuflaje, la tensión viene del doble papel. Estás escondido y cazando al mismo tiempo: mientras escrutas el cuadro en busca de un plano sospechoso, alguien escruta el tuyo.',
          ],
        },
        {
          heading: 'Lo que no existe en ningún otro sitio',
          blocks: [
            'La puntuación premia a los dos bandos de forma opuesta. Quien busca puntúa más cuanto antes encuentra; quien ha dibujado puntúa más cuanto más resiste su casilla. Un retoque que nadie ve da el máximo a su autor.',
            'De ahí nace una manera de jugar muy particular: no se trata de hacer algo bonito, sino de hacerlo exacto. Tomar el color correcto, prolongar una línea en el sitio justo, no dejar ninguna esquina vacía. Son gestos de observación antes que de dibujo.',
            'Para entenderlo en la práctica, lo más sencillo es leer las reglas completas o lanzar una partida rápida: arranca sin ajustes y sin esperar.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Cómo ser imbatible en Retusch',
      date: '2026-08-21',
      summary:
        'Los atajos de teclado, el método de lo general al detalle y el error de las esquinas vacías: tres cosas que separan a un principiante de un jugador al que ya no se encuentra.',
      sections: [
        {
          heading: 'Aprende los atajos y doblarás tu tiempo útil',
          blocks: [
            'Dos minutos de dibujo pasan muy rápido cuando se va a buscar cada herramienta con el ratón. Los atajos recuperan fácilmente veinte o treinta segundos por ronda, y esos segundos son los que dedicarás al detalle.',
            {
              list: [
                'B para el pincel, P para el bote, I para el cuentagotas, H para la mano.',
                '1, 2 y 3 para los tres tamaños de pincel, sin pasar por la barra de herramientas.',
                'Ctrl+Z, o Cmd+Z en Mac, para deshacer trazo a trazo.',
                'Mantener Alt cambia al cuentagotas y devuelve la herramienta anterior en cuanto sueltas. Es el más útil de todos: tomas un color en plena acción sin cambiar nunca de herramienta.',
              ],
            },
            'Una nota sobre los teclados AZERTY: las teclas 1, 2 y 3 funcionan sin Mayúsculas, aunque tu teclado escriba ahí &, é y ". El juego lee la posición de la tecla, no el carácter que produce.',
          ],
        },
        {
          heading: 'Pinta de lo general al detalle',
          blocks: [
            'El error clásico del principiante es empezar por una esquina y esmerarse en ella. El cronómetro se acaba, tres cuartos de la casilla siguen siendo fondo, y el retoque se ve desde diez metros.',
            'El buen método es el contrario. Empieza con un pincel grueso, o con el bote, y coloca las grandes masas de color: el cielo, la pared, la sombra del suelo. En veinte segundos tu casilla ya tiene el aspecto general correcto y deja de leerse como un bloque extraño.',
            'Solo entonces reduce el tamaño y trata las transiciones: la línea del horizonte que atraviesa, el borde de un objeto, el matiz que cambia a media altura. Si falta tiempo, habrás perdido detalle, no la ronda.',
            'Toma siempre los colores con el cuentagotas en lugar de elegirlos en la paleta. Un tono aproximado basta para delatar una casilla entera, mientras que el tono exacto la vuelve invisible.',
          ],
        },
        {
          heading: 'Nunca dejes las esquinas vacías',
          blocks: [
            'Es la pista número uno, y la primera que buscan los jugadores con experiencia. Una casilla con el centro bien tratado pero con las cuatro esquinas al color de fondo dibuja un cuadrado perfecto en la pantalla. Ni siquiera hace falta mirar el dibujo: la forma geométrica salta a la vista.',
            'Acostúmbrate a tratar primero el contorno y luego el centro. Un marco bien fundido perdona un interior aproximado; al revés nunca es cierto.',
            'Desbórdate a propósito por los bordes. El juego recorta tu trazo en el límite de la casilla, así que no arriesgas nada yendo más allá, y es la única forma de asegurarte de que ningún píxel del fondo sobrevive en una esquina.',
          ],
        },
        {
          heading: 'Y cuando te toca buscar',
          blocks: [
            'Barre primero el cuadro entero sin ampliar: una casilla mal fundida parece una mancha, y el zoom solo sirve para confirmar. Después sigue las líneas largas, las que atraviesan la imagen: un borde desplazado un pelo casi siempre es un retoque.',
            'Y recuerda que solo tienes derecho a un error por ronda. Tres segundos de comprobación cuestan menos que la ronda entera.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Un juego de dibujo para jugar entre amigos',
      date: '2026-08-21',
      summary:
        'Por qué Retusch funciona bien en grupo pequeño, cómo invitar pegando un enlace, y por qué basta un jugador para desbloquear los modos de pago para toda la mesa.',
      sections: [
        {
          heading: 'Un juego que no le pide nada a nadie',
          blocks: [
            'El mayor obstáculo de una noche de juegos a distancia es la instalación. Una cuenta que crear, un cliente que descargar, una versión que no coincide: la mitad del grupo se descuelga antes de jugar.',
            'Retusch funciona en el navegador. Tus amigos hacen clic en un enlace, escriben un apodo y se encuentran en la mesa. No hay nada que instalar, nada que crear, y funciona igual de bien en ordenador que en móvil.',
            'Otra cosa que cuenta en grupo: nadie queda apartado por no saber dibujar. El juego no pide representar nada, solo hacer que una casilla encaje con lo que la rodea. Quien dibuja mal pero observa bien gana a menudo.',
          ],
        },
        {
          heading: 'Invitar, en la práctica',
          blocks: [
            'Crea una sala desde la pantalla de inicio. Desmarca la casilla «sala pública» si quieres quedarte entre vosotros: la invitación por enlace se convierte entonces en la única vía de entrada.',
            'Copia el enlace con el botón previsto y pégalo en tu conversación de grupo, tu canal de voz o tu mensajería. Contiene el código de la sala: tus amigos aterrizan directamente en tu mesa.',
            'Puedes ajustar la partida mientras llega la gente: el modo, el número de rondas, el tiempo de dibujo, el tamaño de la casilla e incluso las categorías de imágenes. Nada queda fijado hasta que lanzas.',
          ],
        },
        {
          heading: 'Uno paga, todos juegan',
          blocks: [
            'Dos modos son gratis para siempre: la caza común, que es el modo por defecto, y el modo stream.',
            'Los demás pasarán a ser de pago, pero solo al CREAR una partida. Unirse seguirá siendo gratis, sin condiciones. En la práctica, basta con que una sola persona del grupo haya desbloqueado el juego para que toda la mesa disfrute de todos los modos, sin cuenta y sin pagar nada.',
            'El desbloqueo es una compra única, sin suscripción.',
          ],
        },
        {
          heading: 'Qué modo para qué grupo',
          blocks: [
            'A tres o cuatro, la caza común basta y se entiende en una ronda. A partir de seis se vuelve francamente buena: los hallazgos se encadenan y la pantalla no para de moverse.',
            'Para cerrar una velada, prueba el battle royale: la mesa se vacía ronda tras ronda y la última se juega entre dos. Para un grupo variado, en el que algunos prefieren dibujar tranquilos, el concurso quita toda la presión del reloj de la búsqueda.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Un juego de dibujo para jugar en stream',
      date: '2026-08-21',
      summary:
        'Retusch tiene un modo pensado para el directo, en el que el streamer busca solo mientras su público se esconde. Cómo usarlo, y qué otros modos funcionan ante una audiencia.',
      sections: [
        {
          heading: 'Un modo hecho para eso',
          blocks: [
            'El modo stream invierte los papeles. Todos los jugadores dibujan sobre el mismo cuadro, salvo el anfitrión, que no dibuja en absoluto. Después el anfitrión es el único que busca, mientras su público mira.',
            'Es exactamente la situación que reclama un directo, y por una razón concreta: como el anfitrión no dibuja, su propio escondite no existe, y el chat no ha visto nada que no debiera ver. Los espectadores sí conocen, en cambio, los escondites de los demás jugadores, y pueden verlo pasar de largo tres veces seguidas.',
            'Se escribieron dos reglas para este caso. El streamer no suma puntos ni aparece en la clasificación: la partida se juega entre quienes se esconden, él dirige el espectáculo. Y puede equivocarse tantas veces como quiera, mientras que todos los demás modos se detienen en el primer error. Un directo no puede terminar en un clic desafortunado.',
            'El modo stream es gratis, y seguirá siéndolo.',
          ],
        },
        {
          heading: 'El concurso, la segunda buena idea',
          blocks: [
            'En el modo concurso, todo el mundo repinta la misma casilla del mismo cuadro, y luego cada cual vota el retoque que prefiere. Ya no hay nada que encontrar, solo que juzgar.',
            'Ante una audiencia funciona muy bien, y por la misma razón que el modo stream: no hay nada que encontrar, así que no hay nada que el chat pueda destripar. Las propuestas se comparan lado a lado, la votación da un momento de suspense, y la revelación de los nombres ofrece un cierre limpio. Es además el modo más corto.',
          ],
        },
        {
          heading: 'Y los demás modos',
          blocks: [
            'Nada obliga a jugar un modo dedicado, pero entonces conviene jugar con amigos y no con el chat. En todos los demás modos el streamer dibuja su propia casilla en pantalla: los espectadores la ven construirse y conocen la respuesta antes incluso de que empiece la búsqueda.',
            'Una partida entre amigos, en cambio, se sigue muy bien desde fuera: la fase de dibujo muestra a cada cual construyendo su escondite, y la revelación descubre todas las casillas de golpe, con el nombre de su autor.',
          ],
        },
        {
          heading: 'Hacer entrar al chat en la partida',
          blocks: [
            'El enlace de invitación es lo único que hay que compartir. Pégalo en el chat y los primeros en llegar llenan la mesa: sin cuenta que crear, sin descarga y sin cola.',
            'El límite de jugadores se ajusta en la sala, entre 2 y 10. Con una audiencia numerosa, abre varias partidas cortas en lugar de una larga: una ronda dura menos de tres minutos.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Las novedades de Retusch',
      date: '2026-08-21',
      summary:
        'Los cambios de juego introducidos en Retusch, del más reciente al más antiguo: nuevos modos, nuevas reglas, mejoras en el dibujo y en la búsqueda.',
      sections: [
        {
          heading: 'Agosto de 2026',
          blocks: [
            {
              list: [
                'La casilla que hay que repintar ya no parte de un cuadrado blanco, sino del color dominante de aquello que cubre. Ya no se pierde la mitad del tiempo repintando un fondo que no existe en el cuadro.',
                'El bote de pintura ya no deja un borde claro junto a los trazos: ahora se desliza bajo su borde suavizado.',
                'El battle royale ya no cuenta puntos. El primer jugador descubierto abandona la partida, y cada cual recibe un puesto de ronda mostrado sobre su casilla, del rojo al verde.',
                'El modo stream da al streamer tantos intentos como quiera: su primer fallo ya no cierra la ronda.',
                'En modo espectador ya se puede desplazar y ampliar el cuadro que se está viendo construir.',
                'Las reacciones emoji salen del apodo de su autor, en la lista de jugadores, en lugar de subir desde abajo.',
                'Fuera de la casilla que hay que repintar, el pincel y el bote desplazan la vista: ya no hace falta coger la herramienta mano para moverse.',
                'El nombre del juego, arriba a la izquierda durante una partida, lleva de vuelta al inicio tras confirmación.',
                'El japonés y el coreano se suman a los quince idiomas ya disponibles.',
              ],
            },
          ],
        },
        {
          heading: 'Julio de 2026',
          blocks: [
            {
              list: [
                'Nuevo modo concurso: todo el mundo repinta la misma casilla, y luego cada cual vota el retoque que prefiere.',
                'Nuevo modo stream, pensado para el directo: el anfitrión busca solo mientras su público se esconde.',
                'Las categorías de imágenes sustituyen a los niveles de dificultad. Se marcan las que se quieren ver, y cada categoría muestra un cuadro de ejemplo.',
                'Dos rondas seguidas ya no caen en el mismo cuadro, y las categorías marcadas se alternan de una ronda a otra.',
                'La galería de fin de partida permite volver a ver un retoque trazo a trazo y exportarlo como imagen o como GIF animado.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'De un vistazo',
    allArticles: 'Todos los artículos',
    theRules: 'Las reglas del juego',
    theModes: 'Los modos de juego',
  },
};
